import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Section, Overline, PrimaryButton, OutlineButton } from "../components/UI";
import { SEO } from "../components/SEO";
import { ChevronDown, ArrowUpRight, ShieldCheck } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  RECURRENT PREGNANCY LOSS — Master Implementation                   */
/*  Content supplied by Dr. Tania Gurdip Singh (verbatim).             */
/*  Part 1 loaded. Parts 2 & 3 pending. Reviewed-line withheld until   */
/*  final sign-off.                                                    */
/* ------------------------------------------------------------------ */

const PAGE_TITLE =
  "Recurrent Pregnancy Loss — Dr. Tania Gurdip Singh";
const PAGE_DESC =
  "Recurrent Pregnancy Loss is the spontaneous loss of two or more pregnancies. A disciplined, evidence-based evaluation begins with reconstructing the full history — not with ordering every available test.";

/* Manually curated contents menu (per Dr. Tania's request). */
const TOC = [
  { id: "overview", label: "Every Pregnancy Counts" },
  { id: "patterns", label: "The Pattern Matters" },
  { id: "history", label: "Reconstructing the Story" },
  { id: "documents", label: "Documents to Bring" },
  { id: "investigations-principle", label: "Investigations" },
  { id: "uterus", label: "Uterine Assessment" },
  { id: "aps", label: "APS & Thrombophilia" },
  { id: "treatment", label: "Treatment" },
  { id: "next-pregnancy", label: "Next Pregnancy Care" },
  { id: "prognosis", label: "Prognosis" },
  { id: "faq", label: "Frequently Asked Questions" },
  { id: "evidence-base", label: "Evidence Base" },
  { id: "disclaimer", label: "Medical Disclaimer" },
];

/* FAQs — used for BOTH visible accordion and FAQPage JSON-LD (must match). */
const FAQ_ITEMS = [
  {
    q: "Do biochemical pregnancies count?",
    a: "Yes. In my practice, a pregnancy confirmed by a positive urine or blood hCG test is included, even when it ends before a pregnancy sac becomes visible on ultrasound. Confirmed ectopic and molar pregnancies are evaluated separately. [1]",
  },
  {
    q: "Do the losses have to be consecutive?",
    a: "No. Pregnancy losses do not need to occur one after another. Healthy pregnancies between losses remain important when reconstructing the history and estimating prognosis. [1,3]",
  },
  {
    q: "Is investigation appropriate after two losses?",
    a: "Yes. My approach is to begin a thoughtful evaluation after two confirmed pregnancy losses. This does not mean ordering every available test. The extent of investigation should reflect the woman’s age, pregnancy pattern, medical history and previous results. [1,3,4]",
  },
  {
    q: "When can we try for another pregnancy?",
    a: "After an uncomplicated early miscarriage, there is usually no requirement to wait a fixed number of cycles. Trying again may be considered when the miscarriage has resolved, there is no medical contraindication and both partners feel ready. Separate advice is needed after ectopic pregnancy, molar pregnancy, methotrexate treatment, infection, major surgery or a medically complicated loss. [13,14]",
  },
  {
    q: "Should I start aspirin as soon as the pregnancy test is positive?",
    a: "Not unless there is a recognised indication and a pre-agreed treatment plan. Aspirin and heparin have an established role in appropriately diagnosed obstetric APS. They have not been shown to improve live birth when prescribed routinely for unexplained RPL. [1,6]",
  },
  {
    q: "Does every woman with RPL need progesterone?",
    a: "No. The clearest guideline-supported indication is bleeding in the current early pregnancy, at least one previous miscarriage and an intrauterine pregnancy confirmed by ultrasound. Other use should be discussed individually. [1,5,7,8]",
  },
  {
    q: "Do I need repeated hCG tests?",
    a: "Not always. Serial hCG testing is most useful when the pregnancy location or progression is uncertain, when symptoms are present or before ultrasound can provide a clear answer. Once an appropriately developing intrauterine pregnancy is visible, repeated hCG tests may add little useful information.",
  },
  {
    q: "Will frequent ultrasound scans prevent another miscarriage?",
    a: "No. Ultrasound does not prevent a chromosomal or developmental problem. It can confirm pregnancy location, assess development, guide decisions and provide appropriately timed reassurance.",
  },
  {
    q: "Does stress cause recurrent pregnancy loss?",
    a: "There is no evidence that ordinary stress, work, exercise, sexual intercourse or an argument directly causes an early miscarriage. Recurrent loss can, however, produce severe anxiety, grief, guilt and symptoms of trauma. Those effects deserve care in their own right. [13]",
  },
  {
    q: "Does a normal evaluation mean that nothing is wrong?",
    a: "No. It means that available testing has not found a reliable and treatable explanation. Many biological processes involved in implantation, embryo development and placentation cannot yet be measured accurately in routine clinical practice.",
  },
  {
    q: "Should both partners be evaluated?",
    a: "Yes. The evaluation should consider both partners, but it should not automatically give them identical test panels. The male review may include health, age, medicines, lifestyle, exposures and fertility history. Further testing is selected according to the clinical situation. [1,3]",
  },
  {
    q: "Is IVF with PGT-A the best next step?",
    a: "Not for every couple. IVF with PGT-A has not been shown to improve cumulative live birth or shorten time to pregnancy for all couples with RPL compared with continued natural conception. It may be discussed in selected situations, including coexisting infertility, maternal age above 40 years with a proven aneuploid loss, or another established indication for IVF. [1]",
  },
  {
    q: "What happens if another loss occurs?",
    a: "The immediate priorities are safe medical assessment, confirmation of complete resolution and compassionate care. When pregnancy tissue is available, array-based chromosome testing should be discussed. The result should then be reviewed against the previous investigation and pregnancy sequence before new treatment is added. [1]",
  },
  {
    q: "What should I bring to my consultation?",
    a: "Bring all available: hCG reports. Ultrasound reports and images. Hospital summaries. Histopathology reports. Pregnancy-tissue chromosome results. Previous laboratory investigations. Operative records. Fertility-treatment records. Medicine and supplement lists. A simple chronological summary of every pregnancy is especially useful.",
  },
];

/* Inline citation marker → jumps to first cited reference. */
function Cite({ children }) {
  const m = String(children).match(/(\d+)/);
  const first = m ? m[1] : "";
  return (
    <sup className="text-prestige font-medium">
      <a href={`#ref-${first}`} className="hover:underline">[{children}]</a>
    </sup>
  );
}

/* Accordion row (all closed by default). */
function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  const slug = q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 50);
  return (
    <div className="border-t border-line last:border-b" data-testid={`faq-${slug}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm"
      >
        <span className="font-serif text-lg md:text-xl text-navy leading-snug pt-0.5">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={1.5}
          className={`text-prestige shrink-0 mt-1 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && <div className="pb-6 pr-8 text-[15px] text-stone2 leading-relaxed max-w-3xl">{a}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function RecurrentPregnancyLoss() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <div data-testid="rpl-page">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESC}
        jsonLd={faqJsonLd}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Recurrent Pregnancy Loss", item: "/recurrent-pregnancy-loss" },
        ]}
      />

      {/* Hero */}
      <section className="bg-bone" data-testid="rpl-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <Overline>Recurrent Pregnancy Loss</Overline>
            <div className="h-px w-12 bg-prestige my-6" />
            <h1
              data-testid="rpl-heading"
              className="font-serif italic text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-prestige tracking-tight"
            >
              When &ldquo;Try Again&rdquo; Stops Being a Plan
            </h1>
            <p
              data-testid="rpl-subheading"
              className="text-lg md:text-xl text-stone2 mt-6 leading-relaxed max-w-2xl"
            >
              One pregnancy loss can leave many unanswered questions. After a
              second loss, repeating the same reassurance without reviewing the
              pattern is no longer enough.
            </p>
            <p className="text-lg text-stone2 mt-5 leading-relaxed max-w-2xl">
              Recurrent Pregnancy Loss requires more than a collection of blood
              tests. It requires an accurate reconstruction of every pregnancy,
              a disciplined search for treatable factors, and the restraint to
              avoid investigations or treatments that have not been shown to
              help.
            </p>

            <div className="mt-7 border-l-2 border-prestige pl-5 max-w-xl">
              <p className="text-[15px] text-navy leading-relaxed">
                My approach begins with three principles:
              </p>
              <ol className="mt-3 space-y-1.5 text-[15px] text-navy list-decimal list-inside marker:text-prestige marker:font-medium">
                <li>Every confirmed pregnancy counts.</li>
                <li>Every investigation must have a purpose.</li>
                <li>Treatment must follow evidence, not fear.</li>
              </ol>
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryButton
                to="/contact?type=appointment&reason=rpl"
                testid="rpl-hero-cta-primary"
              >
                Book a Recurrent Pregnancy Loss Consultation
              </PrimaryButton>
              <OutlineButton to="#history" testid="rpl-hero-cta-secondary">
                Understand the Evaluation
              </OutlineButton>
            </div>
          </div>
        </div>
      </section>

      {/* Two-column: contents menu (sticky on desktop) + main body */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Contents menu */}
          <aside
            data-testid="rpl-contents"
            className="lg:col-span-3 lg:sticky lg:top-32"
          >
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
              On this page
            </div>
            <div className="h-px w-8 bg-prestige mt-3 mb-4" />
            <nav aria-label="Contents">
              <ul className="space-y-2.5">
                {TOC.map((t) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      data-testid={`toc-${t.id}`}
                      className="text-[14px] text-navy hover:text-prestige transition-colors leading-snug block"
                    >
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Body */}
          <div className="lg:col-span-9 max-w-3xl">
            {/* Every Pregnancy Counts */}
            <section id="overview" className="scroll-mt-32">
              <Overline>Definition</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Every Pregnancy Counts
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Recurrent Pregnancy Loss, or RPL, is the spontaneous loss of
                  two or more pregnancies. The losses do not need to be
                  consecutive.
                </p>
                <p>
                  In my practice, a pregnancy confirmed by a positive urine or
                  blood hCG test is included, even when it ends before a
                  pregnancy sac is visible on ultrasound. This is commonly
                  called a biochemical pregnancy.
                </p>
                <p>
                  A pregnancy is not &ldquo;too early to count&rdquo; simply
                  because it was not yet visible on a scan. Biochemical
                  pregnancies can provide clinically relevant information. They
                  also represent a genuine loss for the woman or couple
                  experiencing them.<Cite>1–3</Cite>
                </p>
                <p>
                  Confirmed ectopic pregnancies and molar pregnancies are
                  assessed separately. Implantation failure without a positive
                  pregnancy test is also different from Recurrent Pregnancy Loss
                  and requires a separate clinical pathway.<Cite>1,3</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Why You May Have Heard a Different Definition
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  International guidelines do not use one identical definition.
                </p>
                <p>
                  ASRM, ESHRE and the 2025 SOGC guideline recognise Recurrent
                  Pregnancy Loss after two or more losses and include
                  pregnancies confirmed by urine or blood hCG.<Cite>1–3</Cite>
                </p>
                <p>
                  RCOG continues to define recurrent miscarriage formally as
                  three or more first-trimester miscarriages. However, RCOG also
                  advises clinicians to consider a detailed evaluation after two
                  losses when the history suggests that the losses may not be
                  isolated or sporadic events.<Cite>4</Cite>
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                  My clinical position
                </div>
                <p className="mt-3 text-[15px] text-navy leading-relaxed">
                  Two confirmed pregnancy losses are sufficient to begin a
                  thoughtful, individualised evaluation.
                </p>
                <p className="mt-3 text-[15px] text-navy leading-relaxed">
                  Beginning an evaluation does not mean that every available
                  test must be ordered. It means that the history deserves
                  careful review and that the next decision should be based on
                  the pattern, not on a fixed number alone.
                </p>
              </div>
            </section>

            {/* The Pattern Matters */}
            <section id="patterns" className="scroll-mt-32 mt-16">
              <Overline>Clinical Reasoning</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Pattern Matters More Than the Count
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Two women may each have experienced three pregnancy losses.
                  They may not need the same investigations.
                </p>
                <p>
                  The stage at which each pregnancy ended changes the clinical
                  questions that should be asked.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                A Positive Test That Never Reached the First Scan
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  A very early loss should not automatically be attributed to
                  low progesterone, immunity, blood clotting or poor egg
                  quality.
                </p>
                <p>
                  The timing of the positive test, hCG pattern, symptoms,
                  menstrual history, method of conception and confirmation of
                  complete resolution all matter.
                </p>
                <p>
                  A biochemical pregnancy is part of the history. It is not, by
                  itself, a diagnosis.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                A Pregnancy That Stopped Before Heart Activity Was Seen
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Chromosomal abnormalities are a common explanation for early
                  pregnancy loss. The probability is influenced strongly by
                  maternal age.
                </p>
                <p>
                  However, chromosome error is not the only possibility. The
                  complete history determines whether uterine, endocrine,
                  autoimmune, genetic or other factors require investigation.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                A Loss After Heart Activity Was Documented
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The gestational age, fetal measurements, ultrasound
                  progression and any available pregnancy-tissue results become
                  especially important.
                </p>
                <p>
                  A pregnancy that stops after documented heart activity may
                  deserve a different emphasis from a pregnancy that ends
                  shortly after implantation.
                </p>
                <p>The timing guides the investigation. It does not prove the cause.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                A Second-Trimester Loss
              </h3>
              <div className="prose-rpl mt-4">
                <p>A later loss requires its own pathway.</p>
                <p>
                  The review may need to consider cervical insufficiency,
                  uterine structure, fetal abnormalities, placental disease,
                  infection in selected circumstances, and antiphospholipid
                  syndrome.
                </p>
                <p>
                  It should not simply be placed into the same category as
                  repeated very early losses.
                </p>
              </div>
            </section>

            {/* Reconstructing the Story */}
            <section id="history" className="scroll-mt-32 mt-16">
              <Overline>The First Consultation</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Before Ordering Tests, Reconstruct the Story
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  The first consultation is not a hunt for rare diagnoses. It is
                  a reconstruction.
                </p>
                <p>For each pregnancy, I review:</p>
              </div>

              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Whether conception was spontaneous, assisted or followed fertility treatment.",
                  "The date and type of the first positive pregnancy test.",
                  "Serial hCG results, when available.",
                  "The timing and findings of each ultrasound.",
                  "Whether a gestational sac, yolk sac, embryo or fetal heart activity was documented.",
                  "The exact gestational stage at which development stopped.",
                  "Bleeding, pain, fever or other symptoms.",
                  "Medicines, hormones, injections and supplements used.",
                  "Whether the loss occurred naturally or required medical or surgical treatment.",
                  "Histopathology findings.",
                  "Chromosome or genetic results from pregnancy tissue.",
                  "Previous ectopic or molar pregnancies.",
                  "Previous second-trimester losses.",
                  "Previous live births and pregnancy complications.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The wider review also includes:
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Menstrual and ovulatory history.",
                  "Thyroid, glucose and metabolic health.",
                  "Autoimmune disease and previous thrombosis.",
                  "Uterine procedures or pelvic surgery.",
                  "Fertility history.",
                  "Family history of miscarriage, genetic conditions or thrombosis.",
                  "Relevant paternal health, age, medicines and exposures.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Maternal age and the complete sequence of pregnancies remain
                central to prognosis. The number of losses alone never tells the
                whole story.<Cite>1,3</Cite>
              </p>
            </section>

            {/* Documents to Bring */}
            <section id="documents" className="scroll-mt-32 mt-16">
              <Overline>Preparing for the Consultation</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                What I Ask Patients to Bring
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  A chronological record is often more valuable than a thick
                  folder of unorganised reports.
                </p>
                <p>Please bring, wherever available:</p>
              </div>

              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Pregnancy-test photographs or laboratory hCG reports.",
                  "Ultrasound reports and images.",
                  "Hospital or discharge summaries.",
                  "Histopathology reports.",
                  "Pregnancy-tissue chromosome reports.",
                  "Previous blood-test results.",
                  "Operative records.",
                  "Fertility-treatment records.",
                  "A complete list of medicines and supplements used in each pregnancy.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Missing documents should never prevent a consultation. The
                history can still be reconstructed from the information
                available.
              </p>
            </section>

            {/* --- PART 2: INVESTIGATIONS AND TREATMENT ---------------- */}

            {/* Investigations principle */}
            <section id="investigations-principle" className="scroll-mt-32 mt-16">
              <Overline>Investigations</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Investigation Must Earn Its Place
              </h2>
              <div className="prose-rpl mt-5">
                <p>A test should earn its place.</p>
                <p>It should do at least one of three things:</p>
              </div>
              <ol className="mt-3 space-y-1.5 text-[15px] text-navy list-decimal list-inside marker:text-prestige marker:font-medium">
                <li>Explain what may have happened.</li>
                <li>Change treatment.</li>
                <li>Improve counselling for the next pregnancy.</li>
              </ol>
              <div className="prose-rpl mt-5">
                <p>
                  Testing without a clear purpose can create incidental
                  findings, unnecessary treatment and more anxiety.
                </p>
                <p>
                  The aim is not to produce the longest report. The aim is to
                  find an answer without manufacturing one.
                </p>
              </div>
            </section>

            {/* Pregnancy tissue chromosome testing */}
            <section id="tissue-testing" className="scroll-mt-32 mt-16">
              <Overline>Pregnancy-Tissue Testing</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Pregnancy Tissue May Hold the First Clue
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  When pregnancy tissue is available, chromosome testing can
                  identify whether the loss involved an abnormal number or
                  arrangement of chromosomes.
                </p>
                <p>This can be particularly valuable after a second miscarriage.</p>
                <p>
                  A result showing a sporadic chromosome error may explain the
                  loss and prevent the mother from being incorrectly labelled
                  with an immune, hormonal or clotting disorder.
                </p>
                <p>
                  Array-based chromosome testing is generally preferred over
                  traditional tissue karyotyping because it does not require
                  cell culture and is less vulnerable to culture failure.
                </p>
                <p>However, every testing method has limitations.</p>
                <p>
                  Maternal-cell contamination can affect interpretation. No
                  single platform identifies every possible genetic abnormality.
                </p>
                <p>A normal result does not exclude every genetic cause.</p>
                <p>
                  The result must always be interpreted alongside maternal age,
                  ultrasound findings and the previous pregnancy history.<Cite>1,3</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What the Result May Change
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  An abnormal chromosome number may support counselling about
                  age-related or sporadic embryo aneuploidy.
                </p>
                <p>
                  An unbalanced structural chromosome rearrangement may lead to
                  parental chromosome testing and genetic counselling.
                </p>
                <p>
                  A chromosomally normal loss shifts attention towards other
                  possible maternal, paternal or placental factors.
                </p>
                <p>
                  Pregnancy-tissue testing is an investigation. It is not a
                  judgement on the pregnancy or on either parent.
                </p>
              </div>
            </section>

            {/* Uterus */}
            <section id="uterus" className="scroll-mt-32 mt-16">
              <Overline>Uterine Assessment</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Uterus: Shape, Cavity and Scars
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  The uterus must be assessed in women with otherwise
                  unexplained RPL.
                </p>
                <p>The assessment should look beyond its general appearance.</p>
                <p>It should examine:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The external uterine contour.",
                  "The shape of the cavity.",
                  "A uterine septum or other congenital difference.",
                  "Submucosal or cavity-distorting fibroids.",
                  "Endometrial polyps.",
                  "Intrauterine adhesions.",
                  "Retained pregnancy tissue.",
                  "Changes following previous uterine surgery or evacuation procedures.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  A three-dimensional transvaginal ultrasound is often the most
                  informative first examination when available.
                </p>
                <p>
                  Saline-infusion sonography can provide additional detail about
                  the uterine cavity.
                </p>
                <p>
                  Hysteroscopy allows direct inspection and may permit treatment
                  during the same procedure.
                </p>
                <p>
                  MRI is generally reserved for complex anatomy that remains
                  unclear after ultrasound.<Cite>1,3</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Finding an Abnormality Does Not Automatically Mean Surgery
              </h3>
              <div className="prose-rpl mt-4">
                <p>A uterine finding must be clinically relevant.</p>
                <p>
                  A small intramural fibroid that does not distort the cavity is
                  different from a submucosal fibroid occupying the implantation
                  space.
                </p>
                <p>
                  A minor anatomical variation is different from a significant
                  uterine septum.
                </p>
                <p>
                  Surgery should be considered only when the likely benefit
                  outweighs the risks of bleeding, perforation, infection or new
                  scar formation.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                A Uterine Septum: Important, but Not a Simple Decision
              </h3>
              <div className="prose-rpl mt-4">
                <p>A septate uterus is associated with miscarriage.</p>
                <p>
                  The more difficult question is whether removing the septum
                  will improve the chance of live birth for a particular woman.
                </p>
                <p>Observational studies have suggested benefit.</p>
                <p>
                  A small randomised trial did not demonstrate a clear
                  improvement in live birth after septum resection. It also
                  included women with different reproductive histories and was
                  not large enough to settle every clinical question.
                </p>
                <p>
                  For that reason, hysteroscopic septum resection should be
                  discussed through shared decision-making.
                </p>
                <p>The discussion should include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The accuracy of the diagnosis.",
                  "The size and type of septum.",
                  "The stage of previous losses.",
                  "Previous preterm birth or malpresentation.",
                  "Other possible explanations for the losses.",
                  "The limitations of current evidence.",
                  "The risks of surgery.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[15px] text-stone2 leading-relaxed">
                <Cite>1,3,11</Cite>
              </p>
            </section>

            {/* Antiphospholipid Syndrome */}
            <section id="aps" className="scroll-mt-32 mt-16">
              <Overline>Antiphospholipid Syndrome</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Antiphospholipid Syndrome: The Clotting Condition That Matters
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Antiphospholipid syndrome, or APS, is an acquired autoimmune
                  condition.
                </p>
                <p>It is different from inherited thrombophilia.</p>
                <p>
                  APS can be associated with pregnancy loss, thrombosis, fetal
                  death, placental insufficiency and severe early pre-eclampsia.
                </p>
                <p>The principal laboratory tests are:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Lupus anticoagulant.",
                  "Anticardiolipin IgG and IgM.",
                  "Anti-beta-2 glycoprotein-I IgG and IgM.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A single positive test does not establish APS.</p>
                <p>
                  The antibodies must usually remain positive when repeated at
                  least 12 weeks later.
                </p>
                <p>
                  The antibody type, level, persistence and clinical history all
                  matter.
                </p>
                <p>Testing and diagnosis are not the same thing.</p>
                <p>
                  Guidelines differ slightly regarding when testing should
                  begin. The decision should consider the number and timing of
                  losses, previous thrombosis and previous placental
                  complications.<Cite>1,3,4</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Treatment of Confirmed Obstetric APS
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Low-dose aspirin combined with prophylactic heparin has an
                  established role in appropriately diagnosed obstetric APS.
                </p>
                <p>
                  The exact starting point, dose and duration should be
                  individualised.
                </p>
                <p>
                  Women with persistent moderate or high antibody levels, lupus
                  anticoagulant or previous thrombosis may also require input
                  from a haematologist or rheumatologist.
                </p>
                <p>
                  Aspirin and heparin should not be self-started simply because
                  a pregnancy test becomes positive.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* Inherited thrombophilia */}
            <section id="thrombophilia" className="scroll-mt-32 mt-16">
              <Overline>Inherited Thrombophilia</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Inherited Thrombophilia Is Not APS
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Factor V Leiden, the prothrombin gene variant, protein C
                  deficiency, protein S deficiency and antithrombin deficiency
                  are inherited thrombophilias.
                </p>
                <p>
                  Routine testing for these conditions is not recommended solely
                  because a woman has experienced recurrent early pregnancy
                  loss.
                </p>
                <p>Testing may still be appropriate when there is:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "A personal history of venous thrombosis.",
                  "A strong first-degree family history.",
                  "Another recognised haematological indication.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  MTHFR testing should not form part of a routine RPL
                  investigation.
                </p>
                <p>
                  An MTHFR result does not identify a proven cause of recurrent
                  miscarriage and does not determine whether heparin is
                  required.<Cite>1,3,4</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Heparin Without APS
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Low-molecular-weight heparin does not improve live-birth rates
                  when prescribed routinely for unexplained RPL.
                </p>
                <p>
                  It also does not improve live-birth rates when used solely
                  because inherited thrombophilia is present.
                </p>
                <p>
                  In the ALIFE2 randomised trial, live-birth rates were almost
                  identical with and without low-molecular-weight heparin: 72%
                  versus 71%.<Cite>6</Cite>
                </p>
                <p>
                  A blood-thinning injection is not a general treatment for
                  pregnancy loss.
                </p>
              </div>
            </section>

            {/* Thyroid */}
            <section id="thyroid" className="scroll-mt-32 mt-16">
              <Overline>Thyroid</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Thyroid Function: Treat the Disorder, Not the Antibody Alone
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Thyroid function should be assessed according to the pregnancy
                  history, previous tissue results, symptoms and clinical risk
                  factors.
                </p>
                <p>TSH is the principal screening test.</p>
                <p>Free T4 is added when the TSH result is abnormal.</p>
                <p>Overt hypothyroidism should be treated before conception.</p>
                <p>
                  Subclinical hypothyroidism requires interpretation against the
                  laboratory reference range, pregnancy plans and the wider
                  clinical picture.
                </p>
                <p>
                  Current guidelines differ on whether thyroid-peroxidase
                  antibodies should be tested routinely in every woman with RPL.
                </p>
                <p>There is stronger agreement on treatment:</p>
                <p>
                  Levothyroxine should not be prescribed solely to prevent
                  miscarriage when thyroid function is normal and only thyroid
                  antibodies are positive.
                </p>
                <p>
                  The TABLET and T4LIFE randomised trials did not show an
                  improvement in live birth from levothyroxine in euthyroid
                  women with thyroid antibodies.<Cite>1,9,10</Cite>
                </p>
              </div>
            </section>

            {/* Metabolic health */}
            <section id="metabolic" className="scroll-mt-32 mt-16">
              <Overline>Metabolic Health</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Glucose, PCOS and Metabolic Health
              </h2>
              <div className="prose-rpl mt-5">
                <p>Uncontrolled diabetes increases pregnancy risk.</p>
                <p>Well-controlled diabetes is different.</p>
                <p>HbA1c should be considered when there is:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Known diabetes or prediabetes.",
                  "Previous gestational diabetes.",
                  "PCOS.",
                  "Overweight or obesity.",
                  "A strong family history of diabetes.",
                  "Maternal age above 40 years.",
                  "Symptoms or other metabolic risk factors.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  PCOS should not automatically be named as the cause of RPL.
                </p>
                <p>
                  The relevant issues may include insulin resistance, obesity,
                  ovulatory dysfunction and wider metabolic health.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Metformin
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Metformin may be appropriate for diabetes, prediabetes or
                  selected women with PCOS and metabolic dysfunction.
                </p>
                <p>
                  It should not be prescribed to every woman with RPL as a
                  universal miscarriage-prevention medicine.
                </p>
                <p>
                  Evidence that metformin independently prevents recurrent
                  miscarriage remains incomplete.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* Prolactin */}
            <section id="prolactin" className="scroll-mt-32 mt-16">
              <Overline>Prolactin</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Prolactin: Test When the History Points Towards It
              </h2>
              <div className="prose-rpl mt-5">
                <p>Prolactin testing is useful when there is:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Irregular or absent ovulation.",
                  "Galactorrhoea.",
                  "Significant menstrual disturbance.",
                  "Symptoms suggesting pituitary disease.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Routine prolactin testing is not necessary when these features
                  are absent.
                </p>
                <p>
                  A raised result must also be interpreted carefully because
                  stress, medicines and the circumstances of blood collection
                  can affect prolactin levels.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* Male partner */}
            <section id="male" className="scroll-mt-32 mt-16">
              <Overline>The Male Partner</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Male Partner Is Part of the Pregnancy History
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Recurrent pregnancy loss should not be investigated as though
                  reproduction were exclusively female.
                </p>
                <p>The male review may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Age.",
                  "Smoking and alcohol.",
                  "Weight and metabolic health.",
                  "Medicines or anabolic steroids.",
                  "Occupational or environmental exposure.",
                  "Recent high fever.",
                  "Previous fertility.",
                  "Varicocele or other reproductive-urological history.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Standard semen analysis remains appropriate when infertility
                  is also present.
                </p>
                <p>
                  However, ordinary semen parameters alone do not explain most
                  cases of RPL.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Sperm DNA Fragmentation
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Higher sperm DNA fragmentation has been associated with
                  miscarriage and RPL.
                </p>
                <p>Testing may be considered when:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The losses remain unexplained.",
                  "Infertility is also present.",
                  "There are significant male risk factors.",
                  "The result would lead to a reproductive-urology assessment.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Evidence that treating an abnormal fragmentation result
                  prevents another miscarriage remains uncertain.
                </p>
                <p>
                  It should therefore be a selected investigation, not a
                  compulsory test for every couple.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* Chronic endometritis */}
            <section id="endometritis" className="scroll-mt-32 mt-16">
              <Overline>Chronic Endometritis</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Chronic Endometritis: An Area Still Under Investigation
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Chronic endometritis is a persistent inflammatory condition of
                  the endometrium.
                </p>
                <p>
                  Diagnosis usually relies on an endometrial biopsy with
                  identification of plasma cells, often using CD138 staining.
                </p>
                <p>However:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Diagnostic thresholds differ.",
                  "Biopsy timing differs.",
                  "Plasma cells may accompany polyps or retained pregnancy tissue.",
                  "Laboratories do not use one universal definition.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Testing may be considered in selected women with otherwise
                  unexplained RPL, associated infertility, repeated uterine
                  procedures or suspected cavity pathology.
                </p>
                <p>It should not be routine for every patient.</p>
                <p>
                  Earlier observational studies suggested that antibiotics might
                  improve outcomes after chronic endometritis resolved.
                </p>
                <p>
                  More recent randomised evidence has not shown that routine
                  doxycycline improves miscarriage or live-birth rates.
                </p>
                <p>
                  Empirical antibiotics should therefore not be prescribed to
                  every woman with unexplained RPL.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* Tests not recommended */}
            <section id="tests-not-recommended" className="scroll-mt-32 mt-16">
              <Overline>Investigations With Limited Value</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Tests That Commonly Produce More Noise Than Clarity
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  The following are not recommended as routine investigations
                  for every woman with RPL:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "MTHFR mutation testing.",
                  "Broad inherited-thrombophilia panels.",
                  "Natural-killer-cell testing.",
                  "Cytokine testing.",
                  "HLA compatibility testing.",
                  "Broad reproductive-immunology panels.",
                  "Endometrial-receptivity or ERA testing.",
                  "Endometrial microbiome testing.",
                  "Routine mycoplasma or ureaplasma testing.",
                  "Routine ovarian-reserve testing as an explanation for miscarriage.",
                  "Routine serum progesterone testing in an unassisted pregnancy.",
                  "Routine sperm DNA-fragmentation testing without clinical indication.",
                  "Broad infection screening without symptoms or a relevant history.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  A test may become appropriate because of an individual
                  history.
                </p>
                <p>
                  That is different from offering the same commercial panel to
                  every patient.<Cite>1,3,4</Cite>
                </p>
              </div>
            </section>

            {/* Treatment overview */}
            <section id="treatment" className="scroll-mt-32 mt-16">
              <Overline>Treatment</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Treatment Must Follow the Finding
              </h2>
              <div className="prose-rpl mt-5">
                <p>For clarity, treatments can be placed into three groups:</p>
              </div>
              <div className="mt-6 grid md:grid-cols-3 gap-5">
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">Group 1</div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">Established Role</div>
                  <p className="text-[14px] text-stone2 mt-2 leading-relaxed">
                    Supported when the relevant diagnosis is present.
                  </p>
                </div>
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">Group 2</div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">Consider in Selected Cases</div>
                  <p className="text-[14px] text-stone2 mt-2 leading-relaxed">
                    Evidence is incomplete, conflicting or dependent on the
                    clinical situation.
                  </p>
                </div>
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">Group 3</div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">Not Routinely Recommended</div>
                  <p className="text-[14px] text-stone2 mt-2 leading-relaxed">
                    No reliable improvement in live birth has been demonstrated,
                    or the potential harms outweigh uncertain benefit.
                  </p>
                </div>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Treatments With an Established Role
              </h3>
              <div className="prose-rpl mt-4">
                <p>These include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Low-dose aspirin with heparin for appropriately diagnosed obstetric APS.",
                  "Treatment of overt thyroid disease.",
                  "Optimisation of uncontrolled diabetes.",
                  "Treatment of clinically relevant hyperprolactinaemia with ovulatory dysfunction.",
                  "Genetic counselling when a significant chromosome finding is identified.",
                  "Treatment of relevant maternal medical or autoimmune disease.",
                  "Psychological support and structured early-pregnancy care.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The treatment is established because the diagnosis is established.</p>
                <p>
                  It is not prescribed merely because losses remain
                  unexplained.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* Genetic findings */}
            <section id="genetic" className="scroll-mt-32 mt-16">
              <Overline>Genetic Findings</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Genetic Findings: More Than One Route May Be Reasonable
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  When one partner carries a balanced chromosome rearrangement,
                  options may include:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Continued natural conception.",
                  "Prenatal diagnostic testing.",
                  "IVF with preimplantation genetic testing for structural rearrangements, or PGT-SR.",
                  "Donor gametes in selected circumstances.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Natural conception can still result in a healthy live birth.</p>
                <p>
                  PGT-SR may reduce the chance of transferring an embryo with a
                  specific unbalanced rearrangement.
                </p>
                <p>
                  It has not been proved superior to continued natural
                  conception for every carrier.
                </p>
                <p>
                  Genetic counselling should explain the probability of
                  obtaining transferable embryos, the possibility of repeated
                  IVF cycles, cost, time and the limitations of testing.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* Progesterone */}
            <section id="progesterone" className="scroll-mt-32 mt-16">
              <Overline>Progesterone</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Progesterone: Useful in the Right Clinical Setting
              </h2>
              <div className="prose-rpl mt-5">
                <p>Progesterone is not required by every woman with RPL.</p>
                <p>
                  The PROMISE trial did not show a significant live-birth
                  benefit when vaginal progesterone was routinely started after
                  a positive pregnancy test in women with unexplained recurrent
                  miscarriage.<Cite>7</Cite>
                </p>
                <p>The clearest current indication is different.</p>
                <p>
                  NICE recommends vaginal micronised progesterone 400 mg twice
                  daily when:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "There is bleeding in the current early pregnancy.",
                  "At least one previous miscarriage has occurred.",
                  "An intrauterine pregnancy has been confirmed by ultrasound.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  When fetal heart activity is confirmed, NICE advises
                  continuing treatment until 16 completed weeks.<Cite>5</Cite>
                </p>
                <p>
                  The PRISM trial found that the overall difference was not
                  statistically significant, but benefit was seen in the
                  subgroup with early bleeding and previous miscarriage, with
                  the greatest apparent benefit among women with several
                  previous losses.<Cite>8</Cite>
                </p>
                <p>
                  Empirical progesterone without bleeding remains an area for
                  individual discussion.
                </p>
                <p>
                  A low serum progesterone result in an unassisted pregnancy
                  should not automatically trigger supplementation.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* IVF and PGT-A */}
            <section id="ivf" className="scroll-mt-32 mt-16">
              <Overline>IVF &amp; PGT-A</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                IVF and PGT-A: Not the Automatic Next Step
              </h2>
              <div className="prose-rpl mt-5">
                <p>IVF is a fertility treatment.</p>
                <p>It is not automatically a treatment for RPL.</p>
                <p>
                  PGT-A tests embryos for chromosome-number abnormalities before
                  transfer.
                </p>
                <p>
                  It may reduce the chance of transferring an aneuploid embryo
                  in selected IVF patients.
                </p>
                <p>
                  However, in RPL it has not been shown to improve cumulative
                  live birth or shorten the time to successful pregnancy
                  compared with continued natural conception.
                </p>
                <p>
                  Some women may complete an IVF cycle without obtaining a
                  transferable embryo.
                </p>
                <p>Miscarriage can still occur after transfer of a tested embryo.</p>
                <p>Discussion may be reasonable when there is:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Coexisting infertility.",
                  "Maternal age above 40 years.",
                  "A demonstrated pattern of aneuploid losses.",
                  "Another established reason for IVF.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  The decision should be based on the couple&rsquo;s complete
                  reproductive situation, not on fear after the most recent
                  loss.<Cite>1</Cite>
                </p>
              </div>
            </section>

            {/* Treatments not routine */}
            <section id="treatments-not-recommended" className="scroll-mt-32 mt-16">
              <Overline>Treatments With Limited Evidence</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Treatments That Should Not Become Routine
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  The following should not be offered as standard treatment for
                  unexplained RPL:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Aspirin without a recognised indication.",
                  "Heparin without APS or another medical indication.",
                  "Steroids or prednisolone.",
                  "Intralipid infusions.",
                  "Intravenous immunoglobulin.",
                  "G-CSF.",
                  "Lymphocyte immunisation therapy.",
                  "Broad immune-suppression protocols.",
                  "Endometrial scratching.",
                  "Empirical antibiotics.",
                  "Levothyroxine when thyroid function is normal solely because thyroid antibodies are present.",
                  "Metformin without a metabolic indication.",
                  "Routine IVF with PGT-A for every couple.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>These treatments may feel active.</p>
                <p>Activity is not the same as effectiveness.</p>
                <p>
                  When evidence does not support a treatment, restraint is also
                  medical care.<Cite>1,3,4,6,9,10</Cite>
                </p>
              </div>
            </section>

            {/* --- PART 3: NEXT-PREGNANCY CARE, PROGNOSIS, FAQs, DISCLAIMER --- */}

            {/* Next Pregnancy Begins */}
            <section id="next-pregnancy" className="scroll-mt-32 mt-16">
              <Overline>Next-Pregnancy Planning</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Next Pregnancy Begins Before the Positive Test
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  A next-pregnancy plan should be prepared before conception
                  wherever possible.
                </p>
                <p>The plan is not the same for every woman.</p>
                <p>It should be based on:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The timing and pattern of previous losses.",
                  "Pregnancy-tissue chromosome results.",
                  "Maternal age.",
                  "Uterine findings.",
                  "Antiphospholipid-antibody results.",
                  "Thyroid and metabolic health.",
                  "Previous second-trimester or placental complications.",
                  "Fertility history.",
                  "Relevant paternal factors.",
                  "Medicines already being taken.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  The purpose is to decide in advance what should happen before
                  conception, after the first positive test and if bleeding or
                  pain occurs.
                </p>
                <p>
                  This avoids hurried decisions during an already anxious
                  pregnancy.
                </p>
              </div>
            </section>

            {/* Before Conception */}
            <section id="preconception" className="scroll-mt-32 mt-16">
              <Overline>Preconception Care</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Before Conception: Prepare the Ground
              </h2>
              <div className="prose-rpl mt-5">
                <p>Preconception care may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Completing investigations that could change pregnancy management.",
                  "Optimising thyroid disease, diabetes, hypertension or autoimmune disease.",
                  "Reviewing prescription medicines, non-prescription medicines and supplements.",
                  "Taking at least 400 micrograms of folic acid daily, unless a higher dose is advised for a specific medical reason.",
                  "Stopping smoking and avoiding second-hand smoke.",
                  "Avoiding alcohol while trying to conceive and during pregnancy.",
                  "Limiting caffeine to below 200 mg per day.",
                  "Working gradually towards a healthy weight without extreme dieting.",
                  "Reviewing occupational, environmental and recreational exposures.",
                  "Addressing relevant health factors in the male partner.",
                  "Agreeing which medicines should begin before conception and which should begin only after pregnancy is confirmed.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Lifestyle optimisation supports general pregnancy health.</p>
                <p>It cannot guarantee that another miscarriage will not occur.</p>
                <p>
                  A woman should not be made to believe that an imperfect meal,
                  missed exercise session or stressful day caused her previous
                  losses.<Cite>1,4,12</Cite>
                </p>
              </div>
            </section>

            {/* When can we try again */}
            <section id="when-try-again" className="scroll-mt-32 mt-16">
              <Overline>Timing the Next Pregnancy</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                When Can We Try Again?
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  There is no universal rule requiring every couple to wait a
                  fixed number of menstrual cycles after an uncomplicated early
                  miscarriage.
                </p>
                <p>Trying again may be reasonable when:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The miscarriage has been medically confirmed as complete.",
                  "Bleeding and significant pain have resolved.",
                  "There is no evidence of infection.",
                  "The woman feels physically ready.",
                  "Both partners feel emotionally ready.",
                  "Any investigation or treatment that must occur before conception has been addressed.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Timing requires separate advice after:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "An ectopic pregnancy.",
                  "A molar pregnancy.",
                  "Methotrexate treatment.",
                  "Significant anaemia or infection.",
                  "Major surgery.",
                  "A second-trimester loss.",
                  "A medically complicated pregnancy.",
                  "A result that requires genetic or specialist counselling.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Readiness is personal.</p>
                <p>
                  There is no medically superior emotional timetable.<Cite>13,14</Cite>
                </p>
              </div>
            </section>

            {/* Positive Test */}
            <section id="positive-test" className="scroll-mt-32 mt-16">
              <Overline>After the Positive Test</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Positive Test: A Plan, Not a Panic
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  After a positive pregnancy test, the first step is early
                  communication with the treating team.
                </p>
                <p>The individual plan may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Confirming which prescribed medicines should be continued.",
                  "Starting treatment already planned for confirmed APS or another established indication.",
                  "Reviewing thyroid or glucose control.",
                  "Arranging an appropriately timed ultrasound.",
                  "Using serial hCG measurements when the pregnancy location or progression is uncertain.",
                  "Discussing progesterone if bleeding occurs and the clinical criteria are met.",
                  "Planning additional surveillance after previous second-trimester or placental complications.",
                  "Providing a direct route for advice if symptoms develop.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Aspirin, heparin, progesterone, steroids or other medicines
                  should not be self-started simply because the pregnancy test
                  is positive.
                </p>
                <p>The previous loss determines what deserves attention.</p>
                <p>
                  It does not mean that every previous treatment should
                  automatically be repeated.
                </p>
              </div>
            </section>

            {/* Serial hCG */}
            <section id="serial-hcg" className="scroll-mt-32 mt-16">
              <Overline>Serial hCG</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Serial hCG: Useful When It Answers a Question
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Serial hCG measurements are not required in every pregnancy
                  after recurrent loss.
                </p>
                <p>They may be useful when:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The pregnancy is too early for ultrasound.",
                  "Dates are uncertain.",
                  "Bleeding or pain is present.",
                  "The pregnancy location has not been confirmed.",
                  "An ectopic pregnancy must be considered.",
                  "Previous hCG results require follow-up.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>An hCG value should not be interpreted in isolation.</p>
                <p>
                  A single number cannot confirm normal development, diagnose
                  miscarriage or exclude ectopic pregnancy.
                </p>
                <p>
                  Trends, symptoms and ultrasound findings must be considered
                  together.
                </p>
              </div>
            </section>

            {/* Early Scan */}
            <section id="early-scan" className="scroll-mt-32 mt-16">
              <Overline>Early Ultrasound</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Early Scan Should Answer a Clinical Question
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  An ultrasound performed too early may create uncertainty
                  rather than reassurance.
                </p>
                <p>The first scan should be timed according to:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The date of ovulation or embryo transfer, when known.",
                  "Menstrual-cycle regularity.",
                  "Previous pregnancy pattern.",
                  "Symptoms.",
                  "hCG results, when clinically relevant.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  The first purpose is to establish the location of the
                  pregnancy.
                </p>
                <p>
                  The next is to assess development using accepted ultrasound
                  criteria.
                </p>
                <p>
                  When findings are uncertain, a repeat scan after an
                  appropriate interval is safer than reaching a conclusion too
                  early.
                </p>
                <p>Repeated ultrasound examinations do not prevent chromosomal miscarriage.</p>
                <p>
                  Their value lies in accurate assessment, prompt access to care
                  and support during a pregnancy that may feel difficult to
                  trust.<Cite>4</Cite>
                </p>
              </div>
            </section>

            {/* Bleeding */}
            <section id="bleeding" className="scroll-mt-32 mt-16">
              <Overline>Early-Pregnancy Bleeding</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                If Bleeding Occurs
              </h2>
              <div className="prose-rpl mt-5">
                <p>Bleeding in early pregnancy does not always mean miscarriage.</p>
                <p>The priorities are to assess:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The amount and duration of bleeding.",
                  "Pain and its location.",
                  "Haemodynamic stability.",
                  "Pregnancy location.",
                  "Ultrasound findings.",
                  "Whether further hCG monitoring is required.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  For a woman with previous miscarriage who develops
                  early-pregnancy bleeding, NICE recommends vaginal micronised
                  progesterone 400 mg twice daily after an intrauterine
                  pregnancy has been confirmed by ultrasound.
                </p>
                <p>
                  If fetal heart activity is subsequently confirmed, NICE
                  advises continuing treatment until 16 completed weeks.
                </p>
                <p>
                  Progesterone should not replace assessment of bleeding or
                  delay investigation for ectopic pregnancy.<Cite>5</Cite>
                </p>
              </div>

              {/* Urgent red-flags callout */}
              <div className="mt-8 border-l-4 border-terracotta bg-terracotta/5 p-6" data-testid="rpl-urgent-symptoms">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-terracotta">
                  Seek Urgent Medical Assessment For
                </div>
                <ul className="mt-4 space-y-2 text-[15px] text-navy leading-relaxed" role="list">
                  {[
                    "Severe or increasing abdominal pain.",
                    "Marked one-sided pelvic pain.",
                    "Shoulder-tip pain.",
                    "Fainting, collapse or severe dizziness.",
                    "Heavy or rapidly increasing bleeding.",
                    "Fever or offensive discharge.",
                    "Breathlessness, profound weakness or feeling seriously unwell.",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-2.5 h-1 w-1 rounded-full bg-terracotta shrink-0" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Second-trimester */}
            <section id="second-trimester" className="scroll-mt-32 mt-16">
              <Overline>After Second-Trimester Loss</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                After Previous Second-Trimester Loss
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  A previous second-trimester loss should not be managed as
                  though it were simply a later version of an early miscarriage.
                </p>
                <p>The next-pregnancy plan may require:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Review of the previous labour, symptoms and placental findings.",
                  "Assessment of uterine anatomy.",
                  "Cervical-length surveillance.",
                  "Discussion of cervical cerclage when clinically indicated.",
                  "Maternal-fetal-medicine input.",
                  "Surveillance for placental disease or fetal growth concerns.",
                  "A plan for symptoms such as pelvic pressure, fluid leakage, bleeding or contractions.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  The pathway should reflect the suspected mechanism of the
                  previous loss.
                </p>
              </div>
            </section>

            {/* Supportive Care */}
            <section id="supportive-care" className="scroll-mt-32 mt-16">
              <Overline>Supportive Care</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Supportive Care Is Part of Medical Care
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  A pregnancy after recurrent loss can remain frightening even
                  when every result is reassuring.
                </p>
                <p>Supportive care may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "One clearly identified clinical contact.",
                  "Prompt access when symptoms occur.",
                  "Clinicians who know the previous pregnancy history.",
                  "Clear explanations after every test and scan.",
                  "Avoidance of dismissive reassurance.",
                  "Psychological or bereavement support when desired.",
                  "Recognition of the partner’s experience.",
                  "A written plan for uncertainty, bleeding or another loss.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Psychological support is recommended because recurrent loss
                  can be traumatic.
                </p>
                <p>
                  It is not offered because anxiety or grief caused the
                  miscarriages. ASRM and RCOG both place supportive care within
                  appropriate RPL management.<Cite>1,4</Cite>
                </p>
                <p>
                  Support should not depend on whether medicine has found an
                  explanation.
                </p>
              </div>
            </section>

            {/* Prognosis */}
            <section id="prognosis" className="scroll-mt-32 mt-16">
              <Overline>Prognosis</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Hope Must Be Personal, Not Statistical
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Prognosis cannot be expressed accurately through one
                  percentage placed beside the diagnosis.
                </p>
                <p>
                  ESHRE recommends basing prognosis on the woman’s age and her
                  complete pregnancy history, including previous losses, live
                  births and their sequence.
                </p>
                <p>Other relevant factors include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Whether previous losses were chromosomally abnormal or normal.",
                  "The gestational stage of each loss.",
                  "Whether a treatable condition has been identified.",
                  "Fertility and time-to-conception history.",
                  "Relevant maternal and paternal health factors.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  ASRM reports that approximately 50% to 80% of patients with
                  RPL may have a successful subsequent pregnancy attempt without
                  a specific intervention.
                </p>
                <p>That is a broad population range.</p>
                <p>
                  It is not a personalised guarantee and should not be applied
                  without considering age and the complete pregnancy
                  history.<Cite>1,3</Cite>
                </p>
              </div>
            </section>

            {/* Normal Evaluation */}
            <section id="normal-evaluation" className="scroll-mt-32 mt-16">
              <Overline>Unexplained RPL</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                When the Evaluation Is Normal
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  A normal evaluation does not mean that the losses were
                  imaginary.
                </p>
                <p>It does not mean that nothing happened.</p>
                <p>
                  It means that current medicine has not identified a reliable
                  explanation that can be measured and treated.
                </p>
                <p>This is described as unexplained recurrent pregnancy loss.</p>
                <p>
                  The appropriate response is not to fill uncertainty with every
                  available injection, infusion or tablet.
                </p>
                <p>The plan should instead include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Confirming that the evaluation was appropriate for the pregnancy pattern.",
                  "Optimising maternal and paternal health.",
                  "Avoiding treatments without demonstrated benefit.",
                  "Establishing an early-pregnancy pathway.",
                  "Planning pregnancy-tissue chromosome testing if another loss occurs and tissue is available.",
                  "Revisiting the diagnosis when new information appears.",
                  "Providing psychological support.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Unexplained is a statement about the present limits of medical
                  knowledge.
                </p>
                <p>
                  It is not a prediction that the next pregnancy will fail.
                </p>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="scroll-mt-32 mt-16">
              <Overline>Frequently Asked Questions</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Frequently Asked Questions
              </h2>
              <p className="text-[15px] text-stone2 mt-4 leading-relaxed max-w-2xl">
                Answers below are for general education. Please raise questions
                specific to your situation during consultation.
              </p>
              <div className="mt-8" data-testid="rpl-faq-list">
                {FAQ_ITEMS.map((f) => (
                  <FaqRow key={f.q} q={f.q} a={f.a} />
                ))}
              </div>
            </section>

            {/* Closing (replaces the previous CTA block) */}
            <section className="mt-16 bg-navy-deep text-white p-8 md:p-10" data-testid="rpl-closing">
              <Overline className="text-prestige-soft">Closing</Overline>
              <h2 className="font-serif text-2xl md:text-3xl text-white mt-3 tracking-tight leading-snug">
                The Next Pregnancy Deserves a Clearer Plan
              </h2>
              <div className="prose-rpl mt-5 [&>p]:text-white/80">
                <p>Recurrent pregnancy loss cannot always be explained.</p>
                <p>It can always be taken seriously.</p>
                <p>
                  The objective is not to promise an outcome that no doctor can
                  guarantee.
                </p>
                <p>It is to enter the next pregnancy with:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-white/80 leading-relaxed" role="list">
                {[
                  "A more accurate understanding of the previous losses.",
                  "Investigations chosen for a reason.",
                  "Treatment tied to evidence.",
                  "Fewer unnecessary interventions.",
                  "Earlier access to informed care.",
                  "A plan for both reassurance and uncertainty.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3 items-center">
                <Link
                  to="/contact?type=appointment&reason=rpl"
                  data-testid="rpl-cta-book"
                  className="bg-prestige hover:bg-prestige-soft text-navy rounded-full px-6 py-3 text-sm font-medium"
                >
                  Schedule a Recurrent Pregnancy Loss Consultation
                </Link>
                <span className="text-white/60 text-sm italic">
                  Bring your history. Leave with a plan.
                </span>
              </div>
            </section>

            {/* Evidence Base */}
            <section id="evidence-base" className="scroll-mt-32 mt-16">
              <Overline>Evidence Base</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                References
              </h2>
              <p className="mt-4 text-[14px] text-stone2 leading-relaxed">
                Numbered citations correspond to the superscript reference
                markers throughout this page.
              </p>
              <ol
                className="mt-6 space-y-4 text-[14px] text-stone2 leading-relaxed list-none"
                data-testid="rpl-references"
              >
                <li id="ref-1" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[1]</span>
                  Practice Committee of the American Society for Reproductive
                  Medicine. Recurrent pregnancy loss: a committee opinion.
                  <em> Fertility and Sterility.</em> 2026.
                  doi:10.1016/j.fertnstert.2026.03.001. (ASRM)
                </li>
                <li id="ref-2" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[2]</span>
                  Motan T, Cockwell H, Elliott J, et al. Guideline No. 464:
                  Recurrent Pregnancy Loss.
                  <em> Journal of Obstetrics and Gynaecology Canada.</em>{" "}
                  2025;47(12):103167. doi:10.1016/j.jogc.2025.103167. (PubMed)
                </li>
                <li id="ref-3" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[3]</span>
                  ESHRE Guideline Group on Recurrent Pregnancy Loss. ESHRE
                  guideline: recurrent pregnancy loss—update in 2022.
                  <em> Human Reproduction Open.</em> 2023;2023(1):hoad002.
                  (ESHRE)
                </li>
                <li id="ref-4" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[4]</span>
                  Royal College of Obstetricians and Gynaecologists. Recurrent
                  Miscarriage: Green-top Guideline No. 17. Published June 2023.
                  (rcog.org.uk)
                </li>
                <li id="ref-5" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[5]</span>
                  National Institute for Health and Care Excellence. Ectopic
                  pregnancy and miscarriage: diagnosis and initial management.
                  NICE Guideline NG126. Updated 17 June 2026. (Nice)
                </li>
                <li id="ref-6" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[6]</span>
                  Quenby S, Booth K, Hiller L, et al. Heparin for women with
                  recurrent miscarriage and inherited thrombophilia (ALIFE2): an
                  international open-label, randomised controlled trial.
                  <em> The Lancet.</em> 2023;402:54–61.
                  doi:10.1016/S0140-6736(23)00693-1. (PubMed)
                </li>
                <li id="ref-7" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[7]</span>
                  Coomarasamy A, Williams H, Truchanowicz E, et al. A randomized
                  trial of progesterone in women with recurrent miscarriages.
                  <em> New England Journal of Medicine.</em> 2015;373:2141–2148.
                  doi:10.1056/NEJMoa1504927. (PubMed)
                </li>
                <li id="ref-8" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[8]</span>
                  Coomarasamy A, Devall AJ, Cheed V, et al. A randomized trial
                  of progesterone in women with bleeding in early pregnancy.
                  <em> New England Journal of Medicine.</em> 2019;380:1815–1824.
                  doi:10.1056/NEJMoa1813730. (PubMed)
                </li>
                <li id="ref-9" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[9]</span>
                  Dhillon-Smith RK, Middleton LJ, Sunner KK, et al. Levothyroxine
                  in women with thyroid peroxidase antibodies before conception.
                  <em> New England Journal of Medicine.</em> 2019;380:1316–1325.
                  doi:10.1056/NEJMoa1812537. (PubMed)
                </li>
                <li id="ref-10" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[10]</span>
                  van Dijk MM, Vissenberg R, Fliers E, et al. Levothyroxine in
                  euthyroid thyroid-peroxidase-antibody-positive women with
                  recurrent pregnancy loss: the T4LIFE trial.
                  <em> The Lancet Diabetes &amp; Endocrinology.</em>{" "}
                  2022;10:322–329. (PubMed)
                </li>
                <li id="ref-11" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[11]</span>
                  Rikken JFW, Kowalik CR, Emanuel MH, et al. Septum resection
                  versus expectant management in women with a septate uterus:
                  an international multicentre randomised controlled trial.
                  <em> Human Reproduction.</em> 2021;36:1260–1267. (PubMed)
                </li>
                <li id="ref-12" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[12]</span>
                  American College of Obstetricians and Gynecologists. Good
                  Health Before Pregnancy: Prepregnancy Care. Patient FAQ.
                  (ACOG)
                </li>
                <li id="ref-13" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[13]</span>
                  American College of Obstetricians and Gynecologists. Early
                  Pregnancy Loss. Patient FAQ. (ACOG)
                </li>
                <li id="ref-14" className="pl-8 -indent-8">
                  <span className="mono text-prestige mr-2">[14]</span>
                  Royal College of Obstetricians and Gynaecologists. Early
                  Miscarriage. Patient Information. (RCOG)
                </li>
              </ol>
            </section>

            {/* Medical Disclaimer */}
            <section id="disclaimer" className="scroll-mt-32 mt-16 border-t border-line pt-10" data-testid="rpl-disclaimer">
              <Overline>Medical Disclaimer</Overline>
              <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-tight mt-3">
                Medical Disclaimer
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  This page provides general educational information about
                  recurrent pregnancy loss.
                </p>
                <p>
                  It does not replace individual medical assessment, diagnosis,
                  treatment or emergency care.
                </p>
                <p>
                  Investigations, medicines and monitoring must be selected
                  according to the individual pregnancy history and current
                  clinical findings.
                </p>
                <p>
                  Pregnancy-related bleeding, severe abdominal or one-sided
                  pelvic pain, shoulder-tip pain, fainting, heavy bleeding,
                  fever, breathlessness or significant weakness may require
                  urgent medical assessment.
                </p>
              </div>
            </section>

          </div>
        </div>
      </section>
    </div>
  );
}
